import { useState } from "react";
import { useTranslation } from "react-i18next";
import { App, Flex, List, Space, Typography } from "antd";
import { useForm } from "react-hook-form";
import { UploadFile } from "antd/es/upload/interface";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import { HookFormBaseUpload } from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import yup from "@/libraries/yup";
import {
  useCreateDataFileMutation,
  useGetCurrentUserQuery,
} from "@/redux/service";
import { SimpleModalProps } from "@/components_v2/Modal/types";
import { SelectedFile } from "@/views/datablocks/types/types";

const supportedFileFormats = [
  ".pdf",
  ".docx",
  ".pptx",
  ".txt",
  ".xlsx",
  ".csv",
  ".jpg",
  ".jpeg",
  ".png",
  ".tiff",
  ".gif",
  ".webp",
  ".mp4",
];

export const FileProgressStatus = {
  SUCCESS: "SUCCESS",
  IN_PROGRESS: "IN_PROGRESS",
  FAILED: "FAILED",
} as const;

const CreateDataFileModal = ({ open, setOpen }: SimpleModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const { data: currentUser } = useGetCurrentUserQuery();
  const [createDataFile] = useCreateDataFileMutation();
  const [fileStatuses, setFileStatuses] = useState<
    Record<string, SelectedFile>
  >({});
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filesSchema = yup.object().shape({
    files: yup
      .array()
      .of(
        yup
          .mixed<UploadFile>()
          .test("fileType", t("form.validation.fileFormatInvalid"), (file) => {
            if (!file) {
              return true;
            }
            const fileName = file.name.split(".").pop();
            if (!fileName) return true;
            const extension = `.${fileName.toLowerCase()}`;

            return supportedFileFormats.includes(extension);
          })
          .required(t("datablocks.errors.required")),
      )
      .typeError(t("datablocks.errors.notValid"))
      .required(t("datablocks.errors.required"))
      .min(1, t("datablocks.errors.required")),
  });

  const form = useForm<{ files: UploadFile[] }>({
    resolver: yupResolver(filesSchema),
    mode: "onChange",
    defaultValues: {
      files: [],
    },
  });

  const handleSubmit = async (values: {
    files: UploadFile[];
  }): Promise<void> => {
    const { files } = values;
    if (!files || files.length === 0) return;
    setIsSubmitting(true);

    const initialStatuses = files.reduce<Record<string, SelectedFile>>(
      (acc, file) => {
        acc[file.uid] = {
          name: file.name,
          status: FileProgressStatus.IN_PROGRESS,
          file,
        };
        return acc;
      },
      {},
    );

    setFileStatuses(initialStatuses);

    const uploadPromises = Object.keys(initialStatuses).map(async (fileUid) => {
      try {
        const file = initialStatuses[fileUid];
        if (!file || !file.file.originFileObj) return;
        const data = {
          file: file.file.originFileObj,
          name: file.name,
          organization: currentUser?.organization?.id?.toString() || "",
        };
        await createDataFile(data);

        setFileStatuses((current) => ({
          ...current,
          [fileUid]: {
            ...(initialStatuses[fileUid] as SelectedFile),
            status: FileProgressStatus.SUCCESS,
          },
        }));
      } catch (error) {
        setFileStatuses((current) => ({
          ...current,
          [fileUid]: {
            ...(initialStatuses[fileUid] as SelectedFile),
            status: FileProgressStatus.FAILED,
            error: error instanceof Error ? error.message : "Unknown error",
          },
        }));
      }
    });

    await Promise.allSettled(uploadPromises);
    setIsSubmitting(false);

    if (
      Object.values(fileStatuses).some(
        (i) => i.status === FileProgressStatus.FAILED,
      )
    ) {
      setOpenAlertModal(true);
    } else if (
      Object.values(fileStatuses).every(
        (i) => i.status === FileProgressStatus.SUCCESS,
      )
    ) {
      closeModal();
      notification.success({
        message: t("datablocks.labels.allFilesUploaded"),
      });
    }
  };

  const getStatusIcon = (status: SelectedFile["status"]) => {
    switch (status) {
      case FileProgressStatus.SUCCESS:
        return <CheckCircleFilled style={{ color: "#52c41a" }} />;
      case FileProgressStatus.FAILED:
        return <CloseCircleFilled style={{ color: "#f5222d" }} />;
      case FileProgressStatus.IN_PROGRESS:
        return <LoadingOutlined spin style={{ color: "#1890ff" }} />;
      default:
        return null;
    }
  };

  const closeModal = () => {
    setOpen(false);
    setFileStatuses({});
    form.reset();
  };

  return (
    <BaseModal
      title={t("datablocks.labels.uploadFiles")}
      open={open}
      onCancel={closeModal}
      footer={
        <Flex wrap justify="space-between" align="center" gap={"small"}>
          <BaseButton
            variant="tertiary"
            size="small"
            onClick={() => setOpen(false)}
          >
            {t("buttons.cancel")}
          </BaseButton>

          <BaseButton
            variant="primary"
            size="small"
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("buttons.upload")}
          </BaseButton>
        </Flex>
      }
    >
      <HookFormBaseUpload
        name="files"
        disabled={isSubmitting}
        control={form.control}
        multiple={true}
        shouldShowValues={true}
      />

      {Object.keys(fileStatuses).length > 0 && (
        <List
          size="small"
          bordered
          dataSource={Object.values(fileStatuses)}
          renderItem={(item) => (
            <List.Item>
              <Space>
                {getStatusIcon(item.status)}
                <Typography.Text>{item.name}</Typography.Text>
                {item.error && (
                  <Typography.Text type="danger">
                    - {item.error}
                  </Typography.Text>
                )}
              </Space>
            </List.Item>
          )}
        />
      )}

      <BaseModal
        width={500}
        open={openAlertModal}
        onCancel={() => setOpenAlertModal(false)}
        title={t("datablocks.labels.failedToUploadFiles")}
        footer={
          <Flex wrap justify="end" align="center" gap={"small"}>
            <BaseButton onClick={() => setOpenAlertModal(false)}>
              {t("buttons.close")}
            </BaseButton>
          </Flex>
        }
      >
        {t("datablocks.labels.failedToUploadFilesContent")}
      </BaseModal>
    </BaseModal>
  );
};

export default CreateDataFileModal;
